import React from 'react';
import { FormApi } from 'final-form';
import { EnquiryDetail, Actions } from '../../models/Enquiry';
import { useStyles } from '../enquiryFormDrawerStyles';
import { mainTheme } from '../../../Theme';
import { Grid, IconButton, Card, Button, CircularProgress } from '@material-ui/core';
import { FormSpy } from 'react-final-form';
import { MatterAutoCompleteSelector } from '../../../components/MatterAutoCompleteSelector';
import { IAutoCompleteItem } from '../../../typings/autoComplete';
import { SkeletonLoader } from '../../../components/loading/SkeletonLoader';
import { CustomTable } from '../../../components/CustomTable';
import { AnimatedItem } from '../../../components/animations/AnimatedItem';
import { MatterTerminology } from '../../../MatterTerminology';
import { formatNumber } from '../../../helpers/ChartHelper';
import { LightTooltip } from '../components/LightTooltip';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import AutorenewOutlinedIcon from '@material-ui/icons/AutorenewOutlined';
import clsx from 'clsx';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import moment from 'moment';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
import AccountBalanceWalletOutlinedIcon from '@material-ui/icons/AccountBalanceWalletOutlined';
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined';
import HourglassEmptyOutlinedIcon from '@material-ui/icons/HourglassEmptyOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import type { EnquiryFormState } from '../EnquiryFormDrawer';
import { EnquiryFormTab } from '../enquiryFormDrawerConstants';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field } from '../../../helpers/renderCountDebug';

// Same performance fix/reasoning as ActivityTabSection just above - matterDetailsTab() didn't
// even take `values` as a parameter any more (confirmed by reading its full body - everything it
// needs already comes through its own local <FormSpy subscription={{ values: true }}>), so it
// never needed the top-level Form's own values subscription in the first place; it was only
// re-rendering because it was called as a plain function inline, with no component boundary.
interface MatterTabSectionProps {
    form: FormApi<any>;
    state: Pick<EnquiryFormState, 'tab' | 'matterClientName' | 'generalInfoData' | 'financials' | 'currency' | 'isUploadingMatterDocument' | 'sourceSystem'>;
    isWideLayout: boolean;
    isMediumLayout: boolean;
    showMatterIn3rdColumn: boolean;
    enquiryDetail: EnquiryDetail;
    enquiryActionData: Actions | undefined;
    isSubmitting: boolean;
    isMatterLoading: boolean;
    // tslint:disable-next-line: no-any
    fetchMatterDetails: any;
    // tslint:disable-next-line: no-any
    onShowMatterDetails: any;
    // tslint:disable-next-line: no-any
    estimatedFeesColour: any;
    // tslint:disable-next-line: no-any
    uploadEnquiryMatterDocument: any;
}

const matterTabSectionPropsAreEqual = (prev: MatterTabSectionProps, next: MatterTabSectionProps): boolean => (
    prev.form === next.form
    && prev.state.tab === next.state.tab
    && prev.state.matterClientName === next.state.matterClientName
    && prev.state.generalInfoData === next.state.generalInfoData
    && prev.state.financials === next.state.financials
    && prev.state.currency === next.state.currency
    && prev.state.isUploadingMatterDocument === next.state.isUploadingMatterDocument
    && prev.state.sourceSystem === next.state.sourceSystem
    && prev.isWideLayout === next.isWideLayout
    && prev.isMediumLayout === next.isMediumLayout
    && prev.showMatterIn3rdColumn === next.showMatterIn3rdColumn
    && prev.enquiryDetail === next.enquiryDetail
    && prev.enquiryActionData === next.enquiryActionData
    && prev.isSubmitting === next.isSubmitting
    && prev.isMatterLoading === next.isMatterLoading
);

export const MatterTabSection: React.FC<MatterTabSectionProps> = React.memo((componentProps) => {
    const {
        state, isWideLayout, isMediumLayout, showMatterIn3rdColumn, enquiryDetail, enquiryActionData,
        isSubmitting, isMatterLoading, fetchMatterDetails, onShowMatterDetails, estimatedFeesColour,
        uploadEnquiryMatterDocument,
    } = componentProps;
    const classes = useStyles();
        // Explicit bug report: switching between Activity and Further Details (wide layout's
        // own middle-column tabs) was replaying this tab's whole card stagger-in animation
        // every time, even though the Matter column itself never left the screen. Root cause -
        // this div is always mounted (display:none/block below, not a conditional mount), but
        // each AnimatedItem card further down keys off state.tab directly, so ANY state.tab
        // change remounted every one of them regardless of whether Matter was even the tab
        // being switched to. isMatterTabVisible only flips on a genuine hidden<->visible
        // transition (the moment this column/tab actually starts/stops being on screen) - which
        // is what "replay the animation" was always meant to track (see each card's own key).
        const isMatterTabVisible = (isWideLayout || isMediumLayout) ? showMatterIn3rdColumn : state.tab === EnquiryFormTab.matterDetails;

        return (
            <div
                style={{
                    display: isMatterTabVisible ? 'block' : 'none',
                    ...((isWideLayout || isMediumLayout) ? {
                        gridArea: 'third',
                        minWidth: 0,
                        borderLeft: `1px solid ${mainTheme.CommonColor.LightGrey2}`,
                        paddingLeft: 16,
                    } : {}),
                }}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} md={12} xs={12} sm={12}>
                        <FormSpy subscription={{ values: true }}>
                            {/* tslint:disable-next-line: no-shadowed-variable */}
                            {({ values }) => (
                                <div>
                                    {!enquiryDetail.matterFileNumber && enquiryActionData?.canChangeLinkedMatter === false && (
                                        <p className={classes.syncNotice}>
                                            <AutorenewOutlinedIcon className={classes.syncNoticeIcon} />
                                            <span>
                                                The matter is created and linked. Its details are in {state.sourceSystem || 'the source system'} and will appear here after the next sync.
                                            </span>
                                        </p>
                                    )}

                                    {/* Buttons should be encapsulated in FormSpy - While close, we are refering to values and form */}
                                    <div className={classes.matterSelectorWrapper}>
                                        <Field
                                            name="matterSelector"
                                            // tslint:disable-next-line: max-line-length
                                            subscription={{ touched: true, error: true, value: true }}
                                        >
                                            {({ input, meta }) => (
                                                <MatterAutoCompleteSelector
                                                    {...input}
                                                    label="Matter"
                                                    variant="outlined"
                                                    // Outlined needs 'small' to stay the same height as the old
                                                    // standard-variant field - medium (its own default) leaves extra
                                                    // padding around the notched border, reading as unusually tall.
                                                    size="small"
                                                    // tslint:disable-next-line: no-any
                                                    onSelection={(selection: IAutoCompleteItem | any, name: string) => {
                                                        input.onChange(selection);
                                                        if (selection) {
                                                            fetchMatterDetails(selection.id);
                                                        }
                                                    }}
                                                    disabled={!enquiryActionData?.canChangeLinkedMatter}
                                                    value={input.value ? input.value : null}
                                                    error={meta.error && meta.touched}
                                                    helperText={
                                                        meta.error &&
                                                            meta.touched
                                                            ? meta.error
                                                            : ''
                                                    }
                                                    className={classes.textField}
                                                />
                                            )}
                                        </Field>
                                        <IconButton
                                            size="small"
                                            onClick={onShowMatterDetails(values)}
                                            disabled={!values.matterSelector}
                                        >
                                            <OpenInNewIcon />
                                        </IconButton>
                                    </div>
                                </div>
                            )}
                        </FormSpy>
                    </Grid>
                    {isMatterLoading ? (
                        // Explicit bug report: this column previously rendered unconditionally
                        // with state.generalInfoData, which stays undefined until
                        // fetchMatterDetails resolves - nothing covered that gap. Both Grid
                        // items below depend on that same fetch (generalInfoData/financials/
                        // matterClientName are all set together in one setState call), so both
                        // are gated together here rather than separately.
                        <Grid item={true} md={12} xs={12} sm={12}>
                            <SkeletonLoader variant="panel" columns={2} rows={4} />
                        </Grid>
                    ) : (
                        <>
                    <Grid item={true} md={12} xs={12} sm={12}>
                        <CustomTable
                            header={state.matterClientName ? ['Client', state.matterClientName] : undefined}
                            content={state.generalInfoData}
                            options={['client', 'company']}
                        />
                    </Grid>
                    <Grid item={true} md={12} xs={12} sm={12}>
                        <div className={classes.cardWrapper}>
                            {/* Explicit request: same AnimatedItem treatment as elsewhere in this drawer
                                (Quick View widgets, the Details tab's Check Duplicates result) - keyed per
                                state.tab so the stagger replays every time this tab is switched into,
                                not just on the drawer's own first mount. */}
                            <AnimatedItem key={`${isMatterTabVisible}-estimatedCosts`} noFill={true} delay={0 * 30}>
                                <Card className={classes.card}>
                                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipTeal)}>
                                        <AssessmentOutlinedIcon fontSize="small" />
                                    </span>
                                    <div className={classes.financialTileLabel}>
                                        {MatterTerminology.EstimatedCosts}
                                    </div>
                                    <div
                                        className={classes.financialTileValue}
                                        style={{ color: estimatedFeesColour(state.financials) }}
                                    >
                                        {`${state.currency}${state.financials ? formatNumber(state.financials.estimatedCosts) : 0}`}
                                    </div>
                                    <div className={classes.financialTileSubheading}>
                                        {estimatedFeesColour(state.financials) ? 'Over the quoted budget' : 'Quoted budget'}
                                    </div>
                                </Card>
                            </AnimatedItem>
                            {/* Explicit request: same position as the original two-column layout, where
                                this was the top of the second column (i.e. 2nd overall reading left-to-right). */}
                            <AnimatedItem key={`${isMatterTabVisible}-costAgreement`} noFill={true} delay={1 * 30}>
                                <Card className={classes.card}>
                                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipIndigo)}>
                                        <DescriptionOutlinedIcon fontSize="small" />
                                    </span>
                                    <div className={classes.financialTileLabel}>
                                        {MatterTerminology.CostAgreementReceived}
                                    </div>
                                    <div className={classes.financialTileValue}>
                                        {
                                            state.financials && state.financials.costAgreementReceived
                                                // Was 'd MMM YYYY' (lowercase d - moment's day-of-week token, not
                                                // day-of-month) - matches the uppercase 'DD MMM YYYY' every other
                                                // date in this drawer's header panel already uses.
                                                ? moment(state.financials.costAgreementReceived).format('DD MMM YYYY')
                                                : '—'
                                        }
                                    </div>
                                    <div className={classes.financialTileSubheading}>Cost agreement</div>
                                </Card>
                            </AnimatedItem>
                            <AnimatedItem key={`${isMatterTabVisible}-billedFees`} noFill={true} delay={2 * 30}>
                                <Card className={classes.card}>
                                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipGrey)}>
                                        <CheckCircleOutlineIcon fontSize="small" />
                                    </span>
                                    <div className={classes.financialTileLabel}>
                                        {MatterTerminology.Billed + ' ' + MatterTerminology.BilledTimeAndFees}
                                    </div>
                                    <div className={classes.financialTileValue}>
                                        {`${state.currency}${state.financials ? formatNumber(state.financials.billedTimeAndFees) : 0}`}
                                    </div>
                                    <div className={classes.financialTileSubheading}>Already invoiced</div>
                                </Card>
                            </AnimatedItem>
                            <AnimatedItem key={`${isMatterTabVisible}-billedDisbursements`} noFill={true} delay={3 * 30}>
                                <Card className={classes.card}>
                                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipGrey)}>
                                        <ReceiptOutlinedIcon fontSize="small" />
                                    </span>
                                    <div className={classes.financialTileLabel}>
                                        {MatterTerminology.Billed + ' ' + MatterTerminology.Disbursements}
                                    </div>
                                    <div className={classes.financialTileValue}>
                                        {`${state.currency}${state.financials ? formatNumber(state.financials.billedDisbursements) : 0}`}
                                    </div>
                                    <div className={classes.financialTileSubheading}>Already invoiced</div>
                                </Card>
                            </AnimatedItem>
                            <AnimatedItem key={`${isMatterTabVisible}-totalOwing`} noFill={true} delay={4 * 30}>
                                <Card className={classes.card}>
                                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipRed)}>
                                        <AccountBalanceWalletOutlinedIcon fontSize="small" />
                                    </span>
                                    <div className={classes.financialTileLabel}>{MatterTerminology.TotalOwing}</div>
                                    <div className={classes.financialTileValue}>
                                        {`${state.currency}${state.financials ? formatNumber(state.financials.balanceDue) : 0}`}
                                    </div>
                                    <div className={classes.financialTileSubheading}>Outstanding from client</div>
                                </Card>
                            </AnimatedItem>
                            <AnimatedItem key={`${isMatterTabVisible}-trustBalance`} noFill={true} delay={5 * 30}>
                                <Card className={classes.card}>
                                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipTeal)}>
                                        <VerifiedUserOutlinedIcon fontSize="small" />
                                    </span>
                                    <div className={classes.financialTileLabel}>
                                        {MatterTerminology.TrustBalance}
                                    </div>
                                    <div className={classes.financialTileValue}>
                                        {`${state.currency}${state.financials ? formatNumber(state.financials.trustGeneralBalance) : 0}`}
                                    </div>
                                    <div className={classes.financialTileSubheading}>Held on trust</div>
                                </Card>
                            </AnimatedItem>
                            <AnimatedItem key={`${isMatterTabVisible}-unbilledFees`} noFill={true} delay={6 * 30}>
                                <Card className={classes.card}>
                                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipOrange)}>
                                        <HourglassEmptyOutlinedIcon fontSize="small" />
                                    </span>
                                    <div className={classes.financialTileLabel}>
                                        {
                                            // tslint:disable-next-line:max-line-length
                                            MatterTerminology.Unbilled + ' ' + MatterTerminology.UnbilledTimeAndFees
                                        }
                                    </div>
                                    <div className={classes.financialTileValue}>
                                        {`${state.currency}${state.financials ? formatNumber(state.financials.unbilledTimeAndFees) : 0}`}
                                    </div>
                                    <div className={classes.financialTileSubheading}>To be billed</div>
                                </Card>
                            </AnimatedItem>
                            <AnimatedItem key={`${isMatterTabVisible}-unbilledDisbursements`} noFill={true} delay={7 * 30}>
                                <Card className={classes.card}>
                                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipOrange)}>
                                        <ReceiptOutlinedIcon fontSize="small" />
                                    </span>
                                    <div className={classes.financialTileLabel}>
                                        {MatterTerminology.Unbilled + ' ' + MatterTerminology.Disbursements}
                                    </div>
                                    <div className={classes.financialTileValue}>
                                        {`${state.currency}${state.financials ? formatNumber(state.financials.unbilledDisbursements) : 0}`}
                                    </div>
                                    <div className={classes.financialTileSubheading}>To be billed</div>
                                </Card>
                            </AnimatedItem>
                            <AnimatedItem key={`${isMatterTabVisible}-trustControlledMoney`} noFill={true} delay={8 * 30}>
                                <Card className={classes.card}>
                                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipTeal)}>
                                        <LockOutlinedIcon fontSize="small" />
                                    </span>
                                    <div className={classes.financialTileLabel}>{MatterTerminology.TrustControlledMoneyBalance}</div>
                                    <div className={classes.financialTileValue}>
                                        {`${state.currency}${state.financials ? formatNumber(state.financials.trustControlledMoneyBalance) : 0}`}
                                    </div>
                                    <div className={classes.financialTileSubheading}>Controlled money account</div>
                                </Card>
                            </AnimatedItem>
                        </div>
                        {!!state.financials?.billingMethod && (
                            <AnimatedItem key={`${isMatterTabVisible}-billingMethod`} noFill={true} delay={9 * 30}>
                                <div className={classes.financialFooter}>
                                    <div className={classes.financialFooterRow}>
                                        <span className={classes.financialFooterLabel}>{MatterTerminology.BillingMethod}</span>
                                        <span className={classes.financialFooterValue}>{state.financials.billingMethod}</span>
                                    </div>
                                </div>
                            </AnimatedItem>
                        )}
                    </Grid>
                        </>
                    )}
                    <Grid item={true} md={12} xs={12} sm={12}>
                        {/* Explicit request: only Leap tenants - the backend's UploadEnquiryMatterDocumentSaga (and the Document
                            service's UploadMatterDocumentToSourceSystem handler) only upload for a Leap source system; for any
                            other one nothing is sent and the request ends in a timeout failure. */}
                        {enquiryActionData?.canChangeLinkedMatter && state.sourceSystem.toLowerCase() === 'leap' && (
                            <div className={classes.uploadMatterDocumentPanel}>
                                <p className={classes.uploadMatterDocumentHelper}>
                                    Linking a matter doesn't send any documents to it. Select Upload Documents to send this enquiry's history (as a PDF)
                                    and its attached documents to the linked matter in Leap. Each time you select it, a new copy of every document is sent.
                                </p>
                                <LightTooltip
                                    title="Generates a PDF of this enquiry's history and further details, and uploads it along with any attached documents to the linked matter in the source system."
                                >
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        color="primary"
                                        className={classes.uploadMatterDocumentButton}
                                        onClick={() => uploadEnquiryMatterDocument(enquiryDetail)}
                                        disabled={isSubmitting || state.isUploadingMatterDocument}
                                    >
                                        <AnimatedItem key={state.isUploadingMatterDocument ? 'uploading' : 'idle'} noFill={true}>
                                            <span className={classes.uploadMatterDocumentButtonContent}>
                                                {state.isUploadingMatterDocument ? (
                                                    <>
                                                        <CircularProgress size={14} />
                                                        Uploading…
                                                    </>
                                                ) : (
                                                    <>
                                                        <CloudUploadOutlinedIcon fontSize="small" />
                                                        Upload Documents
                                                    </>
                                                )}
                                            </span>
                                        </AnimatedItem>
                                    </Button>
                                </LightTooltip>
                            </div>
                        )}
                    </Grid>
                </Grid>
            </div>
        );
}, matterTabSectionPropsAreEqual);
